import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import DateField from "@/components/DateField";
import TimeOnlyField from "@/components/TimeOnlyField";
import { todayLocal } from "@/lib/timefmt";
import { Plus, X, Users, ChevronDown, ChevronRight, ClipboardList, Copy, BadgeCheck, Undo2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { RAG_META } from "@/lib/rag";
import { taskStatus, STATUS_META } from "@/lib/taskStatus";

const labelCls = "block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5";
const inputCls = "w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm";

export default function BuildTaskForm({ user, types, roles, unitMembers, cfg, myEquip, taskLocs, orgByUser, editTask, prefill, onSaved, onCancelEdit }) {
  const editId = editTask?.id || null;
  const [form, setForm] = useState({ date: todayLocal(), task_type_id: "", start: "", end: "", end_date: "", brief_min: 0, debrief_min: 0, name: "", remarks: "", comments: "" });
  const [participants, setParticipants] = useState([]);
  const [pickRole, setPickRole] = useState("");
  const [pickPerson, setPickPerson] = useState("");
  const [selModes, setSelModes] = useState([]);
  const [selSeqs, setSelSeqs] = useState([]);
  const [selEquip, setSelEquip] = useState([]);
  const [equipMulti, setEquipMulti] = useState(false);
  const [primaryLoc, setPrimaryLoc] = useState(null);
  const [endLoc, setEndLoc] = useState("");
  const [selAddLocs, setSelAddLocs] = useState([]);
  const [locSearch, setLocSearch] = useState("");
  const [airspace, setAirspace] = useState("");
  const [authOfficer, setAuthOfficer] = useState("");
  const [authComments, setAuthComments] = useState("");
  const [authRestrictions, setAuthRestrictions] = useState("");
  const [saving, setSaving] = useState(false);
  const [formOpen, setFormOpen] = useState(true);
  const [formTab, setFormTab] = useState("details");
  const [officers, setOfficers] = useState([]);
  const [dutyWindows, setDutyWindows] = useState({});
  const [fatigueUid, setFatigueUid] = useState(null);
  const [fatigueDay, setFatigueDay] = useState(null);
  const loadedRef = useRef(null);
  const prefillRef = useRef(null);

  const todayStr = todayLocal();
  const nowMin = new Date().getHours() * 60 + new Date().getMinutes();
  const taskLabel = user?.terminology?.task_label || "Task";
  const canAuth = !!user?.auth_officer || user?.role === "admin" || !!user?.admin_level;

  const myTypes = useMemo(
    () => types.filter((t) => t.kind !== "commitment" && (!t.role_ids?.length || t.role_ids.includes(user?.role_id))),
    [types, user],
  );
  const selType = types.find((t) => t.id === form.task_type_id);
  const typeAirspaces = selType?.airspaces || [];
  const typeEquipTypes = selType?.equipment_types || [];
  const shownEquip = typeEquipTypes.length ? myEquip.filter((e) => typeEquipTypes.includes(e.type)) : myEquip;
  const defaultPrimary = () => (taskLocs.some((l) => l.id === user?.location_id) ? user.location_id : "");

  const myScore = (item) => (item.scores || {})[user?.role_id] || { risk: 0, complexity: 0 };
  const avail = (item) => !item.unit_ids?.length || item.unit_ids.includes(user?.org?.unit_id);
  const availModes = (cfg.modes || []).filter(avail);
  const availGroups = (cfg.sequence_groups || [])
    .map((g) => ({ ...g, seqs: (cfg.sequences || []).filter((s) => s.group_id === g.id && avail(s)) }))
    .filter((g) => avail(g) && g.seqs.length > 0);
  const partRoles = useMemo(() => {
    const allowed = selType?.role_ids?.length ? selType.role_ids : roles.map((r) => r.id);
    return roles.filter((r) => allowed.includes(r.id));
  }, [selType, roles]);
  const peopleForRole = unitMembers.filter((m) => m.role_id === pickRole && !participants.some((p) => p.user_id === m.id));

  const addLocGroups = useMemo(() => {
    const prim = primaryLoc === null ? defaultPrimary() : primaryLoc;
    const q = locSearch.trim().toLowerCase();
    const groups = {};
    for (const l of taskLocs) {
      if (l.id === prim || l.id === endLoc) continue;
      if (q && !l.name.toLowerCase().includes(q)) continue;
      (groups[l.type || "Other"] = groups[l.type || "Other"] || []).push(l);
    }
    return Object.entries(groups).sort((a, b) => a[0].localeCompare(b[0]));
  }, [taskLocs, primaryLoc, endLoc, locSearch, user]); // eslint-disable-line react-hooks/exhaustive-deps

  const totals = useMemo(() => {
    let risk = 0;
    let cx = 0;
    (cfg.modes || []).filter((m) => selModes.includes(m.id)).forEach((m) => { risk += myScore(m).risk; cx += myScore(m).complexity; });
    (cfg.sequences || []).filter((s) => selSeqs.includes(s.id)).forEach((s) => { risk += myScore(s).risk; cx += myScore(s).complexity; });
    return { risk: Math.round(risk * 100) / 100, complexity: Math.round(cx * 100) / 100 };
  }, [selModes, selSeqs, cfg, user]); // eslint-disable-line react-hooks/exhaustive-deps

  const resetForm = () => {
    setForm({ date: todayLocal(), task_type_id: "", start: "", end: "", end_date: "", brief_min: 0, debrief_min: 0, name: "", remarks: "", comments: "" });
    setParticipants([]);
    setPickRole("");
    setPickPerson("");
    setSelModes([]);
    setSelSeqs([]);
    setSelEquip([]);
    setEquipMulti(false);
    setPrimaryLoc(null);
    setEndLoc("");
    setSelAddLocs([]);
    setLocSearch("");
    setAirspace("");
    setAuthOfficer("");
    setAuthComments("");
    setAuthRestrictions("");
  };

  useEffect(() => {
    if (!editTask || loadedRef.current === editTask.id) return;
    loadedRef.current = editTask.id;
    const t = editTask;
    setForm({ date: t.date, task_type_id: t.task_type_id, start: t.start, end: t.end, end_date: t.end_date || "", brief_min: t.brief_min, debrief_min: t.debrief_min, name: t.name || "", remarks: t.remarks || "", comments: t.comments || "" });
    setParticipants((t.participants || []).map((p) => ({ user_id: p.user_id, name: p.name, role_id: p.role_id, role_name: p.role_name })));
    setSelModes(t.mode_ids || []);
    setSelSeqs(t.sequence_ids || []);
    setSelEquip(t.equipment_ids || []);
    setEquipMulti((t.equipment_ids || []).length > 1);
    setPrimaryLoc(t.primary_location_id || "");
    setEndLoc(t.end_location_id || "");
    setSelAddLocs(t.additional_location_ids || []);
    setAirspace(t.airspace || "");
    setAuthOfficer(t.auth_officer_id || "");
    setAuthComments(t.auth_comments || "");
    setAuthRestrictions(t.auth_restrictions || "");
    setPickRole("");
    setPickPerson("");
    setLocSearch("");
    setFormOpen(true);
    setFormTab("details");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [editTask?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!prefill || prefillRef.current === prefill._ts) return;
    prefillRef.current = prefill._ts;
    loadedRef.current = null;
    resetForm();
    setForm((f) => ({ ...f, date: prefill.date, start: prefill.start, end: prefill.end }));
    setFormOpen(true);
    setFormTab("details");
  }, [prefill]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!user?.company_id) return;
    api.get(`/org/auth-officers?company_id=${user.company_id}`).then((r) => setOfficers(r.data)).catch(() => {});
  }, [user?.company_id]);

  const crewIds = useMemo(() => [user?.id, ...participants.map((p) => p.user_id)].filter(Boolean).join(","), [participants, user]);
  useEffect(() => {
    if (!user?.company_id || !form.date || !crewIds) return;
    api.get(`/org/duty-windows?company_id=${user.company_id}&date=${form.date}&member_ids=${crewIds}&rag_date=${todayStr}`)
      .then((r) => setDutyWindows(r.data)).catch(() => {});
  }, [user?.company_id, form.date, crewIds]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!fatigueUid) { setFatigueDay(null); return; }
    api.get(`/org/members/${fatigueUid}/day?date=${todayStr}`)
      .then((r) => setFatigueDay(r.data))
      .catch((e) => { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setFatigueUid(null); });
  }, [fatigueUid]); // eslint-disable-line react-hooks/exhaustive-deps

  const pickType = (id) => {
    const t = types.find((x) => x.id === id);
    setForm((f) => ({ ...f, task_type_id: id, brief_min: t?.brief_min ?? 0, debrief_min: t?.debrief_min ?? 0 }));
    setPickRole("");
    setPickPerson("");
    const allowedAir = t?.airspaces || [];
    if (airspace && allowedAir.length && !allowedAir.includes(airspace)) setAirspace("");
    const allowedEq = t?.equipment_types || [];
    if (allowedEq.length) setSelEquip((s) => s.filter((eid) => { const eq = myEquip.find((e) => e.id === eid); return eq && allowedEq.includes(eq.type); }));
  };

  const addParticipant = () => {
    const p = unitMembers.find((m) => m.id === pickPerson);
    if (!p) return;
    setParticipants((ps) => [...ps, { user_id: p.id, name: p.name, role_id: p.role_id, role_name: p.role_name }]);
    setPickPerson("");
  };

  const submit = async () => {
    if (!form.task_type_id || !form.start || !form.end) { toast.error(`Pick a ${taskLabel.toLowerCase()} type and start/stop times`); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        brief_min: Number(form.brief_min) || 0,
        debrief_min: Number(form.debrief_min) || 0,
        name: form.name.trim() || null,
        remarks: form.remarks.trim() || null,
        comments: form.comments.trim() || null,
        end_date: form.end_date || null,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        participant_ids: participants.map((p) => p.user_id),
        mode_ids: selModes,
        sequence_ids: selSeqs,
        equipment_ids: selEquip,
        primary_location_id: primaryLoc === null ? (defaultPrimary() || null) : (primaryLoc || ""),
        end_location_id: endLoc || "",
        additional_location_ids: selAddLocs,
        airspace: airspace || "",
        auth_officer_id: authOfficer || "",
      };
      if (editId) await api.put(`/tasks/${editId}`, payload);
      else await api.post("/tasks", payload);
      toast.success(editId ? `${taskLabel} updated` : `${taskLabel} built`);
      resetForm();
      loadedRef.current = null;
      onSaved(false);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const doAuthorise = async (on) => {
    try {
      await api.post(`/tasks/${editId}/authorise`, { on, comments: authComments || null, restrictions: authRestrictions || null });
      toast.success(on ? `${taskLabel} authorised` : "Authorisation revoked");
      onSaved(true);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const st = editTask ? taskStatus(editTask, null, todayStr, nowMin) : "scheduled";
  const sm = STATUS_META[st];
  const hist = editTask?.history || [];
  const fmtHist = (h) => {
    const d = h.at ? new Date(h.at) : null;
    const dt = d ? `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}` : "";
    return `${dt} | ${h.item} | ${h.old} → ${h.new} | ${h.user_name}`;
  };
  const copyLog = () => {
    navigator.clipboard.writeText(hist.map(fmtHist).join("\n")).then(() => toast.success("Change log copied to clipboard"));
  };

  const showLocs = taskLocs.length > 0 || typeAirspaces.length > 0;
  const showModes = availModes.length > 0 || availGroups.length > 0;
  const typeNeedsAuth = !!(selType?.requires_authorisation || editTask?.requires_authorisation);
  const tabs = [
    ["details", `${taskLabel} Details`],
    ["crew", "Crew"],
    ...(showLocs ? [["locations", "Locations"]] : []),
    ...(showModes ? [["modes", "Modes"]] : []),
    ["auth", "Authorisation"],
    ["changelog", "Change Log"],
  ];

  const crewList = [{ user_id: user?.id, name: `${user?.first_name || ""} ${user?.last_name || ""}`.trim() || user?.email, role_name: "You" }, ...participants];

  return (
    <div className="p-5 rounded-lg border border-slate-800 bg-slate-900/60 mb-6" data-testid="task-form">
      <div className="flex items-center justify-between mb-4">
        <div className="font-heading text-lg font-semibold">{editId ? `Build ${taskLabel} — editing` : `Build ${taskLabel}`}</div>
        <button onClick={() => setFormOpen((o) => !o)} data-testid="task-form-toggle"
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-slate-800 hover:bg-slate-700 text-xs text-slate-300 transition-colors">
          {formOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          {formOpen ? "Hide" : "Show"}
        </button>
      </div>
      {formOpen && (<>
        <div className="flex gap-1 mb-5 border-b border-slate-800 overflow-x-auto" data-testid="task-form-tabs">
          {tabs.map(([id, label]) => (
            <button key={id} onClick={() => setFormTab(id)} data-testid={`task-tab-${id}`}
              className={`px-3.5 py-2 text-sm font-medium -mb-px border-b-2 transition-colors whitespace-nowrap ${formTab === id ? "border-blue-500 text-white" : "border-transparent text-slate-400 hover:text-slate-200"}`}>
              {label}
            </button>
          ))}
        </div>

        {formTab === "details" && (<>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className={labelCls}>{taskLabel} name (optional — defaults to the type)</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="task-name"
                className={inputCls} placeholder={selType?.name || "e.g. Morning sortie"} />
            </div>
            <div>
              <label className={labelCls}>{taskLabel} type</label>
              <select value={form.task_type_id} onChange={(e) => pickType(e.target.value)} data-testid="task-type-select" className={inputCls}>
                <option value="">Select…</option>
                {myTypes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              {myTypes.length === 0 && <p className="text-[11px] text-slate-500 mt-1.5">No task types configured for your role yet — ask an admin.</p>}
            </div>
            <div>
              <label className={labelCls}>Status</label>
              <div className="pt-1.5">
                <span data-testid="task-status-chip" className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-widest border ${sm.cls}`}>{sm.label}</span>
              </div>
            </div>
            <div>
              <label className={labelCls}>{taskLabel} date</label>
              <DateField value={form.date} onChange={(v) => setForm({ ...form, date: v })} testId="task-date" />
            </div>
            <TimeOnlyField label="Start time" testId="task-start" value={form.start} onChange={(v) => setForm({ ...form, start: v })} />
            <TimeOnlyField label="End time" testId="task-end" value={form.end} onChange={(v) => setForm({ ...form, end: v })} />
            <div>
              <label className={labelCls}>End date (optional — multi-day)</label>
              <DateField value={form.end_date} onChange={(v) => setForm({ ...form, end_date: v })} testId="task-end-date" />
            </div>
            <div>
              <label className={labelCls}>Brief time (min)</label>
              <input type="number" min="0" value={form.brief_min} onChange={(e) => setForm({ ...form, brief_min: e.target.value })}
                data-testid="task-brief-min" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Post time (min)</label>
              <input type="number" min="0" value={form.debrief_min} onChange={(e) => setForm({ ...form, debrief_min: e.target.value })}
                data-testid="task-debrief-min" className={inputCls} />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
            {taskLocs.length > 0 && (
              <div>
                <label className={labelCls}>{taskLabel} location</label>
                <select value={primaryLoc === null ? defaultPrimary() : primaryLoc} onChange={(e) => setPrimaryLoc(e.target.value)}
                  data-testid="task-primary-location" className={inputCls}>
                  <option value="">None</option>
                  {taskLocs.map((l) => <option key={l.id} value={l.id}>{l.name}{l.type ? ` — ${l.type}` : ""}</option>)}
                </select>
              </div>
            )}
            {myEquip.length > 0 && (
              <div>
                <div className="flex items-center justify-between">
                  <label className={labelCls}>Equipment{typeEquipTypes.length > 0 ? ` (${typeEquipTypes.join(", ")} only)` : ""}</label>
                  <button type="button" onClick={() => setEquipMulti((m) => !m)} data-testid="task-equip-multi-toggle"
                    className="text-[10px] text-blue-400 hover:text-blue-300 -mt-1">{equipMulti ? "Single select" : "Select multiple"}</button>
                </div>
                {!equipMulti ? (
                  <select value={selEquip[0] || ""} onChange={(e) => setSelEquip(e.target.value ? [e.target.value] : [])}
                    data-testid="task-equipment-single" className={inputCls}>
                    <option value="">None</option>
                    {shownEquip.map((e) => <option key={e.id} value={e.id}>{e.label}{e.type ? ` — ${e.type}` : ""}{e.capacity ? ` · ${e.capacity} pax` : ""}</option>)}
                  </select>
                ) : (
                  <div className="flex flex-wrap gap-2" data-testid="task-equipment">
                    {shownEquip.map((e) => (
                      <button key={e.id} type="button" data-testid={`task-equip-${e.id}`}
                        onClick={() => setSelEquip((s) => s.includes(e.id) ? s.filter((x) => x !== e.id) : [...s, e.id])}
                        className={`px-2.5 py-1 rounded text-xs border transition-colors ${selEquip.includes(e.id) ? "bg-emerald-600/20 text-emerald-300 border-emerald-600/40" : "bg-slate-950 text-slate-400 border-slate-800 hover:border-slate-600"}`}>
                        {e.label}{e.capacity ? ` · ${e.capacity} pax` : ""}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
            <div>
              <label className={labelCls}>{taskLabel} comments</label>
              <textarea value={form.comments} onChange={(e) => setForm({ ...form, comments: e.target.value })} data-testid="task-comments"
                rows={2} className={`${inputCls} resize-y`} placeholder="Comments for the crew…" />
            </div>
            <div>
              <label className={labelCls}>{taskLabel} remarks</label>
              <textarea value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} data-testid="task-remarks"
                rows={2} className={`${inputCls} resize-y`} placeholder="Remarks…" />
            </div>
          </div>
        </>)}

        {formTab === "crew" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div>
              <div className={labelCls}>Add members from your org unit</div>
              <div className="flex flex-wrap items-center gap-2">
                <select value={pickRole} onChange={(e) => { setPickRole(e.target.value); setPickPerson(""); }} data-testid="task-pick-role"
                  className="px-3 py-2 rounded-md bg-slate-950 border border-slate-800 text-sm">
                  <option value="">Select role…</option>
                  {partRoles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
                <select value={pickPerson} onChange={(e) => setPickPerson(e.target.value)} disabled={!pickRole} data-testid="task-pick-person"
                  className="px-3 py-2 rounded-md bg-slate-950 border border-slate-800 text-sm disabled:opacity-40">
                  <option value="">Select person…</option>
                  {peopleForRole.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
                <button onClick={addParticipant} disabled={!pickPerson} data-testid="task-add-participant"
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-slate-800 hover:bg-slate-700 text-xs disabled:opacity-40">
                  <Plus className="h-3.5 w-3.5" /> Add
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3" data-testid="task-participants">
                {participants.length === 0 && <div className="text-xs text-slate-500">No crew added yet — you'll be listed as the lead.</div>}
                {participants.map((p) => {
                  const dw = dutyWindows[p.user_id];
                  return (
                    <div key={p.user_id} className="p-2.5 rounded-md bg-slate-950/60 border border-slate-800" data-testid={`task-crew-${p.user_id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-medium inline-flex items-center gap-1.5"><Users className="h-3 w-3 text-slate-500" /> {p.name}</span>
                        <button onClick={() => setParticipants((ps) => ps.filter((x) => x.user_id !== p.user_id))}
                          data-testid={`task-remove-participant-${p.user_id}`} className="text-slate-500 hover:text-rose-300"><X className="h-3 w-3" /></button>
                      </div>
                      <div className="text-[10px] text-slate-500 mt-0.5">{p.role_name || ""}</div>
                      <div className="text-[10px] font-mono-num text-slate-400 mt-0.5" data-testid={`task-crew-duty-${p.user_id}`}>
                        Duty {form.date}: {dw?.start ? `${dw.start}–${dw.end}` : "—"}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div>
              <div className={labelCls}>Member fatigue today — click for detail</div>
              <div className="space-y-1.5" data-testid="task-crew-fatigue">
                {crewList.map((m) => {
                  const w = dutyWindows[m.user_id];
                  const meta = w?.rag ? RAG_META[w.rag] : null;
                  return (
                    <button key={m.user_id} onClick={() => setFatigueUid(m.user_id)} data-testid={`task-crew-rag-${m.user_id}`}
                      className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md bg-slate-950/60 border border-slate-800 hover:border-slate-600 transition-colors text-left">
                      <span className="text-xs text-slate-200">{m.name}{m.role_name === "You" ? " (you)" : ""}</span>
                      {meta ? (
                        <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-widest ${meta.cls}`}>
                          <span className={`inline-block h-1.5 w-1.5 rounded-full mr-1.5 align-middle ${meta.dot}`} />
                          {meta.label}{w?.score_total != null ? ` · ${Number(w.score_total).toFixed(1)}` : ""}
                        </span>
                      ) : <span className="text-[10px] text-slate-500">No entry</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {formTab === "locations" && showLocs && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="task-locations">
            <div>
              <label className={labelCls}>Start location</label>
              <select value={primaryLoc === null ? defaultPrimary() : primaryLoc} onChange={(e) => setPrimaryLoc(e.target.value)}
                data-testid="task-start-location" className={inputCls}>
                <option value="">None</option>
                {taskLocs.map((l) => <option key={l.id} value={l.id}>{l.name}{l.type ? ` — ${l.type}` : ""}</option>)}
              </select>
              <p className="text-[11px] text-slate-500 mt-1">Defaults to your profile location.</p>
            </div>
            <div>
              <label className={labelCls}>End location</label>
              <select value={endLoc} onChange={(e) => setEndLoc(e.target.value)} data-testid="task-end-location" className={inputCls}>
                <option value="">None</option>
                {taskLocs.map((l) => <option key={l.id} value={l.id}>{l.name}{l.type ? ` — ${l.type}` : ""}</option>)}
              </select>
            </div>
            {typeAirspaces.length > 0 && (
              <div>
                <label className={labelCls}>Airspace</label>
                <select value={airspace} onChange={(e) => setAirspace(e.target.value)} data-testid="task-airspace" className={inputCls}>
                  <option value="">None</option>
                  {typeAirspaces.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
            )}
            <div>
              <label className={labelCls}>Additional locations (grouped by type)</label>
              <input value={locSearch} onChange={(e) => setLocSearch(e.target.value)} data-testid="task-addloc-filter"
                placeholder="Type to filter, e.g. BN…" className={`${inputCls} mb-1.5`} />
              <div className="max-h-36 overflow-y-auto rounded-md border border-slate-800 bg-slate-950" data-testid="task-addloc-list">
                {addLocGroups.length === 0 && <div className="px-2.5 py-2 text-xs text-slate-500">No matching locations.</div>}
                {addLocGroups.map(([type, items]) => (
                  <div key={type}>
                    <div className="px-2.5 py-1 text-[10px] uppercase tracking-widest text-slate-500 font-mono-num bg-slate-900/80 sticky top-0">{type}</div>
                    {items.map((l) => (
                      <label key={l.id} className="flex items-center gap-2 px-2.5 py-1.5 text-xs text-slate-300 hover:bg-slate-900 cursor-pointer" data-testid={`task-addloc-${l.id}`}>
                        <input type="checkbox" className="accent-blue-600 h-3.5 w-3.5" checked={selAddLocs.includes(l.id)}
                          onChange={() => setSelAddLocs((s) => s.includes(l.id) ? s.filter((x) => x !== l.id) : [...s, l.id])} />
                        {l.name}
                      </label>
                    ))}
                  </div>
                ))}
              </div>
              {selAddLocs.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2" data-testid="task-addloc-selected">
                  {selAddLocs.map((id) => (
                    <span key={id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-sky-600/15 text-sky-300 border border-sky-600/40 text-[10px]">
                      {(taskLocs.find((x) => x.id === id) || {}).name || id}
                      <button onClick={() => setSelAddLocs((s) => s.filter((x) => x !== id))} data-testid={`task-addloc-remove-${id}`}
                        className="text-sky-400/70 hover:text-rose-300"><X className="h-3 w-3" /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {formTab === "modes" && showModes && (<>
          {availModes.length > 0 && (
            <div>
              <div className={labelCls}>Modes (multi-select)</div>
              <div className="flex flex-wrap gap-2" data-testid="task-modes">
                {availModes.map((m) => {
                  const sc = myScore(m);
                  const on = selModes.includes(m.id);
                  return (
                    <button key={m.id} type="button" data-testid={`task-mode-${m.id}`}
                      onClick={() => setSelModes((ms) => on ? ms.filter((x) => x !== m.id) : [...ms, m.id])}
                      className={`px-3 py-1.5 rounded-md text-xs border transition-colors ${
                        on ? "bg-blue-600/20 text-blue-300 border-blue-600/50" : "bg-slate-950 text-slate-400 border-slate-800 hover:border-slate-600"}`}>
                      {m.name} <span className="opacity-60 font-mono-num">R{sc.risk} · C{sc.complexity}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {availGroups.length > 0 && (
            <div className="mt-4 space-y-2" data-testid="task-sequences">
              <div className={labelCls}>Sequences</div>
              {availGroups.map((g) => {
                const allSel = g.seqs.every((s) => selSeqs.includes(s.id));
                return (
                  <div key={g.id} className="rounded-md border border-slate-800 bg-slate-950/40 overflow-hidden">
                    <label className="flex items-center gap-2 px-3 py-2 bg-slate-900/60 cursor-pointer text-sm font-medium">
                      <input type="checkbox" className="accent-blue-600 h-4 w-4" checked={allSel}
                        data-testid={`seq-group-${g.id}`}
                        onChange={() => setSelSeqs((ss) => allSel
                          ? ss.filter((x) => !g.seqs.some((s) => s.id === x))
                          : [...new Set([...ss, ...g.seqs.map((s) => s.id)])])} />
                      {g.name}
                      <span className="text-[10px] text-slate-500 font-mono-num ml-auto">{g.seqs.filter((s) => selSeqs.includes(s.id)).length}/{g.seqs.length}</span>
                    </label>
                    <div className="px-3 py-2 flex flex-wrap gap-2">
                      {g.seqs.map((s) => {
                        const sc = myScore(s);
                        const on = selSeqs.includes(s.id);
                        return (
                          <button key={s.id} type="button" data-testid={`task-seq-${s.id}`}
                            onClick={() => setSelSeqs((ss) => on ? ss.filter((x) => x !== s.id) : [...ss, s.id])}
                            className={`px-2.5 py-1 rounded-md text-xs border transition-colors ${
                              on ? "bg-violet-600/20 text-violet-300 border-violet-600/50" : "bg-slate-950 text-slate-400 border-slate-800 hover:border-slate-600"}`}>
                            {s.name} <span className="opacity-60 font-mono-num">R{sc.risk} · C{sc.complexity}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {(selModes.length > 0 || selSeqs.length > 0) && (
            <div className="mt-3 text-xs font-mono-num text-slate-400" data-testid="task-risk-totals">
              {taskLabel} totals for your role — Risk: <span className="text-blue-300">{totals.risk}</span> · Complexity: <span className="text-violet-300">{totals.complexity}</span>
            </div>
          )}
        </>)}

        {formTab === "auth" && (
          <div className="space-y-4 max-w-xl" data-testid="task-auth-tab">
            {!form.task_type_id ? (
              <p className="text-sm text-slate-500">Pick a {taskLabel.toLowerCase()} type on the Details tab first.</p>
            ) : !typeNeedsAuth ? (
              <p className="text-sm text-slate-500">This {taskLabel.toLowerCase()} type does not require authorisation — nothing to do here.</p>
            ) : !editId ? (
              <p className="text-sm text-slate-500">Save the {taskLabel.toLowerCase()} first — authorisation happens once it's committed.</p>
            ) : (<>
              <div className="flex items-center gap-2 flex-wrap">
                <span className={labelCls + " mb-0"}>Current status:</span>
                <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-widest border ${sm.cls}`}>{sm.label}</span>
                {editTask?.authorised_by_name && <span className="text-xs text-slate-400">by {editTask.authorised_by_name}</span>}
              </div>
              <div>
                <label className={labelCls}>Authorisation officer (from org)</label>
                <select value={authOfficer} onChange={(e) => setAuthOfficer(e.target.value)} data-testid="task-auth-officer" className={inputCls}>
                  <option value="">Any in-scope Auth Officer</option>
                  {officers.map((o) => <option key={o.id} value={o.id}>{o.position ? `${o.position} ` : ""}{o.name}{orgByUser[o.id] ? ` — ${orgByUser[o.id]}` : ""}</option>)}
                </select>
                <p className="text-[11px] text-slate-500 mt-1">Saved with the {taskLabel.toLowerCase()}. Any in-scope Auth Officer can still sign off.</p>
              </div>
              <div>
                <label className={labelCls}>Auth Officer comments</label>
                <textarea value={authComments} onChange={(e) => setAuthComments(e.target.value)} data-testid="task-auth-comments"
                  rows={2} disabled={!canAuth} className={`${inputCls} resize-y disabled:opacity-50`} placeholder="Comments recorded with the authorisation…" />
              </div>
              <div>
                <label className={labelCls}>Auth Officer restrictions</label>
                <textarea value={authRestrictions} onChange={(e) => setAuthRestrictions(e.target.value)} data-testid="task-auth-restrictions"
                  rows={2} disabled={!canAuth} className={`${inputCls} resize-y disabled:opacity-50`} placeholder="e.g. Day VMC only…" />
              </div>
              <div className="flex gap-2">
                {canAuth && editTask?.status === "committed" && (
                  <button onClick={() => doAuthorise(true)} data-testid="task-auth-authorise-btn"
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-violet-600 hover:bg-violet-500 text-white text-sm font-medium transition-colors">
                    <BadgeCheck className="h-4 w-4" /> Authorise
                  </button>
                )}
                {canAuth && editTask?.status === "authorised" && (
                  <button onClick={() => doAuthorise(false)} data-testid="task-auth-revoke-btn"
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-slate-800 hover:bg-amber-900/40 text-slate-300 hover:text-amber-300 text-sm transition-colors">
                    <Undo2 className="h-4 w-4" /> Revoke authorisation
                  </button>
                )}
                {!canAuth && <p className="text-[11px] text-slate-500">Only configured Auth Officers (in scope) can authorise.</p>}
              </div>
            </>)}
          </div>
        )}

        {formTab === "changelog" && (
          <div data-testid="task-changelog">
            <div className="flex items-center justify-between mb-2">
              <div className={labelCls + " mb-0"}>Most recent first</div>
              <button onClick={copyLog} disabled={!hist.length} data-testid="task-changelog-copy"
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-slate-800 hover:bg-slate-700 text-xs text-slate-300 disabled:opacity-40 transition-colors">
                <Copy className="h-3.5 w-3.5" /> Copy to clipboard
              </button>
            </div>
            {!editId ? (
              <div className="text-sm text-slate-500">No changes yet — the log starts when you save the {taskLabel.toLowerCase()}.</div>
            ) : hist.length === 0 ? (
              <div className="text-sm text-slate-500">No changes recorded yet.</div>
            ) : (
              <div className="rounded-md border border-slate-800 bg-slate-950/60 divide-y divide-slate-800/60 max-h-72 overflow-y-auto">
                {hist.map((h, i) => (
                  <div key={i} className="px-3 py-1.5 text-[11px] font-mono-num text-slate-300" data-testid={`task-changelog-row-${i}`}>{fmtHist(h)}</div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="flex gap-2 mt-5 pt-4 border-t border-slate-800">
          <button onClick={submit} disabled={saving} data-testid="task-submit-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium transition-colors disabled:opacity-50">
            <ClipboardList className="h-4 w-4" /> {saving ? "Saving…" : `Save ${taskLabel.toLowerCase()}`}
          </button>
          {editId && (
            <button onClick={() => { resetForm(); loadedRef.current = null; onCancelEdit(); }} data-testid="task-cancel-edit"
              className="px-4 py-2.5 rounded-md bg-slate-800 hover:bg-slate-700 text-sm">Cancel</button>
          )}
        </div>
      </>)}

      <Dialog open={!!fatigueUid} onOpenChange={(o) => !o && setFatigueUid(null)}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-md" data-testid="task-fatigue-popup">
          <DialogHeader><DialogTitle className="font-heading">{fatigueDay?.name || "Member"} — fatigue today</DialogTitle></DialogHeader>
          {!fatigueDay ? (
            <div className="text-sm text-slate-500">Loading…</div>
          ) : (
            <div>
              <div className="text-xs text-slate-400 mb-3">
                Duty today: <span className="font-mono-num text-slate-200">{fatigueDay.duty?.start || "—"}–{fatigueDay.duty?.end || "—"}</span>
              </div>
              {!fatigueDay.entry ? (
                <div className="text-sm text-slate-500">No fatigue entry today.</div>
              ) : (
                <>
                  <div className="flex items-center gap-2.5 mb-3" data-testid="fatigue-popup-score">
                    <span className="text-3xl font-mono-num">{Number(fatigueDay.entry.score_total).toFixed(1)}</span>
                    {RAG_META[fatigueDay.entry.rag] && (
                      <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-widest ${RAG_META[fatigueDay.entry.rag].cls}`}>
                        <span className={`inline-block h-1.5 w-1.5 rounded-full mr-1.5 align-middle ${RAG_META[fatigueDay.entry.rag].dot}`} />
                        {RAG_META[fatigueDay.entry.rag].label}
                      </span>
                    )}
                  </div>
                  <div className="space-y-1 max-h-64 overflow-y-auto" data-testid="fatigue-popup-breakdown">
                    {(fatigueDay.entry.breakdown || []).map((b, i) => (
                      <div key={i} className="flex items-center justify-between gap-2 px-2.5 py-1.5 rounded bg-slate-950/60 text-xs">
                        <span className="text-slate-300 capitalize">{b.question.replace(/_/g, " ")}</span>
                        <span className="flex items-center gap-2 shrink-0">
                          <span className="text-slate-400">{b.label}</span>
                          <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase ${RAG_META[b.rag]?.cls || ""}`}>{b.rag}</span>
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
